import Point from '@mui/icons-material/Place';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, NavigationControl, Popup, Source } from 'react-map-gl/maplibre';

import { VITE_MAPLIBRE_STYLE_URL } from '@/constants/env';
import PointPopup from '@/sections/create-order/dependencies/PointPopup';
import { Destination } from '@/types/parcel/createOrder/create.interface';
import { NearestPoint, Schedule } from '@/types/parcel/createOrder/p99.interface';

import type { MapRef } from 'react-map-gl/maplibre';

import 'maplibre-gl/dist/maplibre-gl.css';

interface MapBoxMapProps {
  p99?: NearestPoint[];
  points?: Destination[];
  center?: [number, number];
  zoom?: number;
  /** Opcional: habilita arrastrar los pines de origen/destino y devuelve la nueva coordenada.
   *  Si no se pasa (p.ej. CreateOrderPage), los pines quedan fijos como hasta ahora. */
  onPointDragEnd?: (locationId: string, lngLat: { lng: number; lat: number }) => void;
  /** Opcional: anima los cambios de cámara (flyTo/fitBounds) como el legacy. Default: salto instantáneo. */
  animate?: boolean;
}

const fitBounds = (
  bounds: [number, number, number, number],
  width = 800,
  height = 600,
  padding = 60,
) => {
  const WORLD_DIM = { width: 1024, height: 512 };
  const ZOOM_MAX = 22;

  const latRad = (lat: number) => {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  };

  const zoom = (mapPx: number, worldPx: number, fraction: number) =>
    Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);

  const latFraction = (latRad(bounds[3]) - latRad(bounds[1])) / Math.PI;
  const lngFraction = (bounds[2] - bounds[0]) / 360;

  const latZoom = zoom(height - padding * 2, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(width - padding * 2, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
};

const createArcLine = (
  origin: { lng: number; lat: number },
  destination: { lng: number; lat: number },
) => {
  if (isNaN(origin.lng) || isNaN(origin.lat) || isNaN(destination.lng) || isNaN(destination.lat)) {
    return null;
  }

  const p0 = [origin.lng, origin.lat];
  const p2 = [destination.lng, destination.lat];

  // Calculate Midpoint
  const m = [(p0[0] + p2[0]) / 2, (p0[1] + p2[1]) / 2];

  // Calculate Vector from P0 to P2
  const dx = p2[0] - p0[0];
  const dy = p2[1] - p0[1];

  // Coordinate distance
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0 || isNaN(len)) return null;

  // Normalized perpendicular vector (oriented northwards for upward arc)
  let nx = -dy / len;
  let ny = dx / len;

  if (ny < 0) {
    nx = -nx;
    ny = -ny;
  }

  // Offset control point by 28% of coordinate distance for a more pronounced arc
  const offset = len * 0.28;
  const p1 = [m[0] + nx * offset, m[1] + ny * offset];

  // Generate 50 points along the quadratic Bezier spline
  const coordinates: [number, number][] = [];
  const steps = 50;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const mt = 1 - t;
    const x = mt * mt * p0[0] + 2 * mt * t * p1[0] + t * t * p2[0];
    const y = mt * mt * p0[1] + 2 * mt * t * p1[1] + t * t * p2[1];
    coordinates.push([x, y]);
  }

  return {
    type: 'Feature' as const,
    properties: {},
    geometry: {
      type: 'LineString' as const,
      coordinates,
    },
  };
};

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

const MapBoxMap: React.FC<MapBoxMapProps> = ({
  p99 = [],
  points = [],
  center,
  onPointDragEnd,
  animate = false,
}) => {
  const theme = useTheme();
  const mapRef = useRef<MapRef | null>(null);

  const initialViewState = useMemo(
    () => ({
      longitude: center?.[0] ?? -58.3816,
      latitude: center?.[1] ?? -34.6037,
      zoom: 10,
    }),
    [center],
  );

  const [viewState, setViewState] = useState(initialViewState);
  const [selectedPoint, setSelectedPoint] = useState<NearestPoint | null>(null);

  const validPoints = useMemo(
    () =>
      (points || []).filter((point): point is Destination =>
        Boolean(
          point &&
          point.lat &&
          point.lng &&
          !isNaN(parseFloat(point.lat)) &&
          !isNaN(parseFloat(point.lng)),
        ),
      ),
    [points],
  );

  // Actualizar el centro del mapa cuando cambie el país del usuario
  useEffect(() => {
    if (center && !validPoints.length) {
      if (animate && mapRef.current) {
        mapRef.current.flyTo({ center: [center[0], center[1]], zoom: 10, duration: 1000 });
      } else {
        setViewState({ longitude: center[0], latitude: center[1], zoom: 10 });
      }
    }
  }, [center, validPoints.length, animate]);

  useEffect(() => {
    if (!validPoints.length) return;

    if (validPoints.length === 1) {
      const lng = parseFloat(validPoints[0].lng!);
      const lat = parseFloat(validPoints[0].lat!);
      if (animate && mapRef.current) {
        mapRef.current.flyTo({ center: [lng, lat], zoom: 15, duration: 1000 });
      } else {
        setViewState({ longitude: lng, latitude: lat, zoom: 15 });
      }
      return;
    }

    const bounds = validPoints.reduce<[number, number, number, number]>(
      ([minLng, minLat, maxLng, maxLat], point) => {
        const lng = parseFloat(point.lng!);
        const lat = parseFloat(point.lat!);
        return [
          Math.min(minLng, lng),
          Math.min(minLat, lat),
          Math.max(maxLng, lng),
          Math.max(maxLat, lat),
        ];
      },
      [Infinity, Infinity, -Infinity, -Infinity],
    );

    if (animate && mapRef.current) {
      mapRef.current.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: 60, duration: 1000, maxZoom: 15 },
      );
    } else {
      const centerLng = (bounds[0] + bounds[2]) / 2;
      const centerLat = (bounds[1] + bounds[3]) / 2;
      const zoom = fitBounds(bounds);
      setViewState({ longitude: centerLng, latitude: centerLat, zoom });
    }
  }, [validPoints, animate]);

  const formatOpeningHours = (schedule: Schedule) =>
    Object.entries({
      sunday: 'Domingo',
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
    }).map(([key, label]) => {
      const day = schedule[key as keyof Schedule];
      if (!day || typeof day !== 'object' || day.closed) return { day: label, hours: 'Cerrado' };
      return { day: label, hours: `${day.opens} - ${day.closes}` };
    });

  const getScheduleStatus = (schedule: Schedule) => {
    const todayKey = new Date()
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase() as keyof Schedule;
    const today = schedule[todayKey];

    if (!today || typeof today !== 'object') {
      return { status: 'Cerrado', message: 'Sin información' };
    }

    return today.closed
      ? { status: 'Cerrado', message: `Abre a las ${today.opens}` }
      : { status: 'Abierto', message: `Cierra a las ${today.closes}` };
  };

  const scheduleInfo = useMemo(() => {
    if (!selectedPoint?.schedule) return { status: 'Cerrado', message: '-' };
    return getScheduleStatus(selectedPoint.schedule);
  }, [selectedPoint]);

  const routeGeoJSON = useMemo<GeoJSON.FeatureCollection>(() => {
    if (validPoints.length < 2) return EMPTY_GEOJSON;
    const originPoint = validPoints[0];
    const destPoint = validPoints[1];
    if (!originPoint.lat || !originPoint.lng || !destPoint.lat || !destPoint.lng) {
      return EMPTY_GEOJSON;
    }

    const oLng = parseFloat(originPoint.lng);
    const oLat = parseFloat(originPoint.lat);
    const dLng = parseFloat(destPoint.lng);
    const dLat = parseFloat(destPoint.lat);

    if (isNaN(oLng) || isNaN(oLat) || isNaN(dLng) || isNaN(dLat)) {
      return EMPTY_GEOJSON;
    }

    const arcFeature = createArcLine({ lng: oLng, lat: oLat }, { lng: dLng, lat: dLat });

    if (!arcFeature) return EMPTY_GEOJSON;

    return {
      type: 'FeatureCollection',
      features: [arcFeature],
    };
  }, [validPoints]);

  const validP99 = useMemo(
    () =>
      (p99 || []).filter(
        (point) =>
          point &&
          typeof point.longitude === 'number' &&
          typeof point.latitude === 'number' &&
          !isNaN(point.longitude) &&
          !isNaN(point.latitude),
      ),
    [p99],
  );

  const markers = useMemo(
    () =>
      validP99.map((point) => (
        <Marker
          key={point.locationId}
          longitude={point.longitude}
          latitude={point.latitude}
          offset={[0, -10]}
        >
          <div
            className="map-marker"
            style={{
              backgroundColor: theme.palette.primary.main,
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: '2px solid white',
              cursor: 'pointer',
              boxShadow: '0 0 8px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPoint(point);
            }}
          />
        </Marker>
      )),
    [validP99, theme.palette.primary.main],
  );

  const pointsMarkers = useMemo(
    () =>
      validPoints.map((point, index) => (
        <Marker
          key={point.locationId || `${point.lat}-${point.lng}-${index}`}
          longitude={parseFloat(point.lng!)}
          latitude={parseFloat(point.lat!)}
          offset={[0, 10]}
          anchor={onPointDragEnd ? 'bottom' : undefined}
          draggable={!!onPointDragEnd}
          onDragEnd={(e) =>
            onPointDragEnd?.(point.locationId ?? '', { lng: e.lngLat.lng, lat: e.lngLat.lat })
          }
        >
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {!!onPointDragEnd && (
              <small
                style={{
                  fontWeight: 'bold',
                  lineHeight: '12px',
                  textAlign: 'center',
                  color: '#12344A',
                  background: 'rgba(255,255,255,0.85)',
                  borderRadius: 6,
                  padding: '2px 6px',
                  marginBottom: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                Coloca el pin sobre
                <br />
                la ubicación exacta
              </small>
            )}
            <Point
              color={index === 0 ? 'primary' : 'secondary'}
              fontSize="large"
              className="map-marker"
            />
          </span>
        </Marker>
      )),
    [validPoints, onPointDragEnd],
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        '& .map-marker': {
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'scale(1.2)',
          },
        },
        '& .mapboxgl-popup-content': {
          padding: 2,
          borderRadius: 2,
          minWidth: 200,
        },
      }}
    >
      <Map
        ref={mapRef}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        mapStyle={VITE_MAPLIBRE_STYLE_URL}
        attributionControl={false}
      >
        <div style={{ position: 'absolute', top: 10, left: 10 }}>
          <NavigationControl />
        </div>

        {pointsMarkers}
        {markers}

        <Source id="arc-route-source" type="geojson" data={routeGeoJSON}>
          <Layer
            id="arc-route-layer"
            type="line"
            layout={{
              'line-join': 'round',
              'line-cap': 'round',
            }}
            paint={{
              'line-color': '#6ace3b',
              'line-width': 4,
              'line-opacity': routeGeoJSON.features.length > 0 ? 1 : 0,
            }}
          />
        </Source>

        {selectedPoint && (
          <Popup
            longitude={selectedPoint.longitude}
            latitude={selectedPoint.latitude}
            closeOnClick={false}
            onClose={() => setSelectedPoint(null)}
            closeButton={false}
          >
            <PointPopup
              title={selectedPoint.businessName}
              status={scheduleInfo.status}
              schedule={scheduleInfo.message}
              address={selectedPoint.address}
              openingHours={formatOpeningHours(selectedPoint.schedule!)}
              onClose={() => setSelectedPoint(null)}
            />
          </Popup>
        )}
      </Map>
    </Box>
  );
};

export default MapBoxMap;
